import React, { useState, useEffect } from 'react';
import './NetWorthCard.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return token ? { 'x-auth-token': token } : {};
};

function NetWorthCard({ accountBreakdown }) {
  const [cryptoData, setCryptoData] = useState(null);
  const [stockData, setStockData] = useState(null);
  const [tillerData, setTillerData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = getAuthHeaders();
        const [cryptoRes, stockRes, tillerRes] = await Promise.all([
          fetch(`${API_URL}/api/crypto`, { headers }),
          fetch(`${API_URL}/api/stocks`, { headers }),
          fetch(`${API_URL}/api/networth`, { headers })
        ]);

        if (cryptoRes.ok) {
          const data = await cryptoRes.json();
          setCryptoData(data);
        }

        if (stockRes.ok) {
          const data = await stockRes.json();
          setStockData(data);
        }

        if (tillerRes.ok) {
          const data = await tillerRes.json();
          setTillerData(data);
        }
      } catch (err) {
        console.error('Failed to fetch data for net worth:', err);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Get Tiller data - only cash (checking/savings), brokerage is tracked by stocks API
  const cashTotal = tillerData?.cashTotal || 0;
  const tillerLiabilities = tillerData?.liabilities || 0;

  // Stocks API has live prices for brokerage holdings (don't use Tiller brokerage - it's duplicate)
  const stocksTotal = stockData?.totalValue || 0;
  const cryptoTotal = cryptoData?.totalValue || 0;

  // Total net worth = Cash - Liabilities + Stocks + Crypto
  const totalNetWorth = cashTotal - tillerLiabilities + stocksTotal + cryptoTotal;

  const formatNumber = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="net-worth-card">
      <div className="net-worth-main">
        <div className="net-worth-label">net_worth</div>
        <div className="net-worth-value">{formatNumber(totalNetWorth)}</div>
      </div>
      <div className="net-worth-breakdown">
        <div className="breakdown-item">
          <span className="breakdown-label">cash</span>
          <span className="breakdown-value">${formatNumber(cashTotal - tillerLiabilities)}</span>
        </div>
        <div className="breakdown-item">
          <span className="breakdown-label">stocks</span>
          <span className="breakdown-value">
            ${formatNumber(stocksTotal)}
            {stockData?.change !== undefined && stockData.change !== 0 && (
              <span className={`change-badge ${stockData.change >= 0 ? 'positive' : 'negative'}`}>
                {stockData.change >= 0 ? '+' : ''}{stockData.change.toFixed(1)}%
              </span>
            )}
          </span>
        </div>
        <div className="breakdown-item">
          <span className="breakdown-label">crypto</span>
          <span className="breakdown-value">
            ${formatNumber(cryptoTotal)}
            {cryptoData?.change24h !== undefined && (
              <span className={`change-badge ${cryptoData.change24h >= 0 ? 'positive' : 'negative'}`}>
                {cryptoData.change24h >= 0 ? '+' : ''}{cryptoData.change24h.toFixed(1)}%
              </span>
            )}
          </span>
        </div>
      </div>
      {tillerData && (
        <div className="tiller-breakdown">
          <div className="tiller-item">
            <span className="tiller-label">checking/savings</span>
            <span className="tiller-value positive">${formatNumber(cashTotal)}</span>
          </div>
          <div className="tiller-item">
            <span className="tiller-label">credit cards</span>
            <span className="tiller-value negative">-${formatNumber(tillerLiabilities)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default NetWorthCard;
